import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface MessageThread {
  id: string;
  peerId: string;
  peerName: string;
  avatarUrl?: string;
  lastSnippet: string;
  ts: string;
  unreadCount?: number;
}

export const useMessageThreads = () => {
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setThreads([]);
        return;
      }

      // Fetch messages where user is sender or recipient
      const { data: messages, error: fetchError } = await supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (fetchError) {
        console.error('Error fetching message threads:', fetchError);
        setError(fetchError.message);
        return;
      }

      if (!messages || messages.length === 0) {
        setThreads([]);
        return;
      }

      // Get unique user IDs we need profiles for
      const userIds = new Set<string>();
      messages.forEach((msg) => {
        if (msg.sender_id !== user.id) userIds.add(msg.sender_id);
        if (msg.recipient_id !== user.id) userIds.add(msg.recipient_id);
      });

      // Fetch profiles for all those users
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, farm_name, avatar_url')
        .in('id', Array.from(userIds));

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

      // Group messages by conversation partner
      const threadMap = new Map<string, MessageThread>();

      messages.forEach((msg) => {
        // Determine the peer (the other person in the conversation)
        const isRecipient = msg.recipient_id === user.id;
        const peerId = isRecipient ? msg.sender_id : msg.recipient_id;
        const peer = profileMap.get(peerId);

        // Skip if we already have a more recent message from this thread
        if (threadMap.has(peerId)) {
          return;
        }

        threadMap.set(peerId, {
          id: `thread_${peerId}`,
          peerId,
          peerName: peer?.farm_name || 'Unknown User',
          avatarUrl: peer?.avatar_url,
          lastSnippet: msg.content?.substring(0, 50) || 'No message content',
          ts: msg.created_at,
          unreadCount: isRecipient && !msg.is_read ? 1 : 0,
        });
      });

      setThreads(Array.from(threadMap.values()));
    } catch (err) {
      console.error('Error in fetchThreads:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();

    // Set up realtime subscription for new messages
    const channel = supabase
      .channel('message_threads')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        },
        () => {
          console.log('Message change detected, refetching threads');
          fetchThreads();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    threads,
    loading,
    error,
    refetch: fetchThreads,
  };
};
